/**
 * GoalsPage — Inline-formulär för att skapa och redigera mål.
 *
 * Mönster: card under header (som Verksamheter-sidan).
 * - "Nytt mål"-knapp togglar inline-form-card under headern
 * - Klick på Redigera-knapp på ett mål-kort öppnar samma inline-form förifyllt (edit-läge)
 * - CreateGoalModal används inte längre
 *
 * Roller med create/update-behörighet: super_admin, customer_admin, employee
 */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { TrendingUp, Plus, X, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { toast } from 'sonner';
import { calculateProgress } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';

// ── Constants ──────────────────────────────────────────────────────────────────

const CATEGORIES = [
  { value: 'arbetsmarknad_matchning',      label: 'Arbetsmarknad och matchning' },
  { value: 'sprak_utbildning',             label: 'Språk och utbildning' },
  { value: 'etablering_orientering',       label: 'Etablering och orientering' },
  { value: 'social_stabilisering',         label: 'Social stabilisering' },
  { value: 'arbetsformaga_arbetslivsforb', label: 'Arbetsförmåga och arbetslivsförberedelse' },
  { value: 'sjalvstandighet_struktur',     label: 'Självständighet och struktur' },
  { value: 'overgang_nasta_steg',          label: 'Övergång till nästa steg' },
  { value: 'ovrigt',                       label: 'Övrigt' },
];

const PRIORITIES = [
  { value: 'low',    label: 'Låg' },
  { value: 'medium', label: 'Medel' },
  { value: 'high',   label: 'Hög' },
];

const WRITE_ROLES = ['super_admin', 'customer_admin', 'employee'];

const selectCls = (err) =>
  `h-10 w-full px-3 border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring ${
    err ? 'border-destructive focus:ring-destructive' : 'border-input'
  }`;

// ── Sub-goal ID helper ─────────────────────────────────────────────────────────

let _sgCounter = 0;
const newSgId = () => `sg_${++_sgCounter}_${Date.now()}`;

// ── Form state builders ────────────────────────────────────────────────────────

const buildEmpty = () => ({
  title:          '',
  participant_id: '',
  description:    '',
  category:       '',
  start_date:     '',
  due_date:       '',
  priority:       '',
  assigned_to:    '',
  sub_goals:      [],
  tags:           '',
  assessment_id:  '',
});

const fromGoal = (g) => ({
  title:          g.title          || '',
  participant_id: g.participant_id || '',
  description:    g.description    || '',
  category:       g.category       || '',
  start_date:     g.start_date     || '',
  due_date:       g.due_date       || '',
  priority:       g.priority       || '',
  assigned_to:    g.assigned_to    || '',
  sub_goals:      (g.sub_goals || []).map((sg) => ({
    id:    sg.id || newSgId(),
    title: sg.title || '',
  })),
  tags:           g.tags           || '',
  assessment_id:  g.assessment_id  || '',
});

// ── GoalForm ───────────────────────────────────────────────────────────────────

const GoalForm = ({ initial, participants, orgUsers, onSave, onCancel, isEdit }) => {
  const { user }                      = useAuth();
  const [form, setForm]               = useState(initial);
  const [errors, setErrors]           = useState({});
  const [submitted, setSubmitted]     = useState(false);
  const [submitting, setSub]          = useState(false);
  const [advOpen, setAdvOpen]         = useState(false);

  const set = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    if (submitted && (field === 'title' || field === 'participant_id')) {
      setErrors((e) => ({ ...e, [field]: value ? '' : e[field] }));
    }
  };

  const validate = (f) => {
    const errs = {};
    if (!f.title.trim())   errs.title         = 'Titel är obligatorisk';
    if (!f.participant_id) errs.participant_id = 'Deltagare är obligatorisk';
    const sgErrs = {};
    f.sub_goals.forEach((sg, i) => {
      if (!sg.title.trim()) sgErrs[i] = 'Delmålets titel krävs';
    });
    if (Object.keys(sgErrs).length) errs.sub_goals = sgErrs;
    return errs;
  };

  const hasErrors = (errs) =>
    Object.keys(errs).filter((k) => k !== 'sub_goals').some((k) => errs[k]) ||
    Object.keys(errs.sub_goals || {}).length > 0;

  const addSubGoal = () =>
    setForm((f) => ({ ...f, sub_goals: [...f.sub_goals, { id: newSgId(), title: '' }] }));

  const updateSubGoal = (idx, title) => {
    setForm((f) => ({
      ...f,
      sub_goals: f.sub_goals.map((sg, i) => (i === idx ? { ...sg, title } : sg)),
    }));
    if (submitted) {
      setErrors((e) => {
        const sg = { ...(e.sub_goals || {}) };
        if (title.trim()) delete sg[idx];
        else sg[idx] = 'Delmålets titel krävs';
        return { ...e, sub_goals: Object.keys(sg).length ? sg : undefined };
      });
    }
  };

  const removeSubGoal = (idx) => {
    setForm((f) => ({ ...f, sub_goals: f.sub_goals.filter((_, i) => i !== idx) }));
    if (submitted) {
      setErrors((e) => {
        const sg = { ...(e.sub_goals || {}) };
        const reindexed = {};
        Object.entries(sg).forEach(([k]) => {
          const ki = Number(k);
          if (ki < idx) reindexed[ki] = sg[ki];
          else if (ki > idx) reindexed[ki - 1] = sg[ki];
        });
        return { ...e, sub_goals: Object.keys(reindexed).length ? reindexed : undefined };
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;
    setSub(true);
    try {
      const payload = {
        title:          form.title.trim(),
        description:    form.description.trim() || null,
        participant_id: form.participant_id,
        org_id:         user?.org_id || 'default',
        due_date:       form.due_date || null,
        ...(form.assessment_id ? { assessment_id: form.assessment_id } : {}),
      };
      if (isEdit) {
        await api.patch(`/goals/${initial.id}`, payload);
        toast.success('Mål uppdaterat!');
      } else {
        await api.post('/goals', payload);
        toast.success('Mål skapat!');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunde inte spara mål');
    } finally {
      setSub(false);
    }
  };

  return (
    <Card
      className="border-2 animate-in fade-in-0 slide-in-from-top-2 duration-200"
      style={{ borderColor: '#F9CF3F' }}
      data-testid="goal-inline-form"
    >
      <CardContent className="pt-5 pb-6 px-6">
        <form onSubmit={handleSubmit} noValidate data-testid="goal-form">

          {/* ══ Sektion 1 — Grundinfo ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">
            Grundinfo
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">

            {/* Titel — full width */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="g-title">
                Titel <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <Input
                id="g-title"
                data-testid="goal-title-input"
                placeholder="Vad är målet?"
                value={form.title}
                onChange={(e) => set('title', e.target.value)}
                aria-required="true"
                aria-invalid={!!errors.title}
                className={errors.title ? 'border-destructive focus-visible:ring-destructive' : ''}
                autoFocus
              />
              {errors.title && (
                <p role="alert" className="text-xs text-destructive">{errors.title}</p>
              )}
            </div>

            {/* Deltagare */}
            <div className="space-y-1.5">
              <Label htmlFor="g-participant">
                Deltagare <span className="text-destructive" aria-hidden="true">*</span>
              </Label>
              <select
                id="g-participant"
                data-testid="goal-participant-select"
                value={form.participant_id}
                onChange={(e) => set('participant_id', e.target.value)}
                className={selectCls(errors.participant_id)}
                aria-required="true"
                aria-invalid={!!errors.participant_id}
              >
                <option value="">Välj deltagare...</option>
                {participants.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              {errors.participant_id && (
                <p role="alert" className="text-xs text-destructive">{errors.participant_id}</p>
              )}
            </div>

            {/* Kategori */}
            <div className="space-y-1.5">
              <Label htmlFor="g-category">Kategori</Label>
              <select
                id="g-category"
                data-testid="goal-category-select"
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
                className={selectCls(false)}
              >
                <option value="">Välj kategori (valfritt)</option>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>

            {/* Beskrivning — full width */}
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="g-description">Beskrivning</Label>
              <Textarea
                id="g-description"
                data-testid="goal-description-input"
                placeholder="Beskriv målet..."
                value={form.description}
                onChange={(e) => set('description', e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* ══ Sektion 2 — Planering ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4 mt-6">
            Planering
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <div className="space-y-1.5">
              <Label htmlFor="g-start-date">Startdatum</Label>
              <Input
                id="g-start-date"
                type="date"
                value={form.start_date}
                onChange={(e) => set('start_date', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-due-date">Deadline</Label>
              <Input
                id="g-due-date"
                type="date"
                value={form.due_date}
                onChange={(e) => set('due_date', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-priority">Prioritet</Label>
              <select
                id="g-priority"
                value={form.priority}
                onChange={(e) => set('priority', e.target.value)}
                className={selectCls(false)}
              >
                <option value="">Valfritt</option>
                {PRIORITIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="g-assigned">Ansvarig</Label>
              <select
                id="g-assigned"
                value={form.assigned_to}
                onChange={(e) => set('assigned_to', e.target.value)}
                className={selectCls(false)}
              >
                <option value="">Valfritt</option>
                {orgUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            </div>
          </div>

          {/* ══ Sektion 3 — Delmål ══ */}
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3 mt-6">
            Delmål
          </p>
          <div className="space-y-2 mb-2" data-testid="sub-goals-list">
            {form.sub_goals.map((sg, idx) => (
              <div key={sg.id}>
                <div className="flex items-center gap-2">
                  <Input
                    data-testid={`sub-goal-input-${idx}`}
                    placeholder={`Delmål ${idx + 1}`}
                    value={sg.title}
                    onChange={(e) => updateSubGoal(idx, e.target.value)}
                    aria-label={`Delmål ${idx + 1}`}
                    aria-invalid={!!(errors.sub_goals?.[idx])}
                    className={
                      errors.sub_goals?.[idx]
                        ? 'border-destructive focus-visible:ring-destructive'
                        : ''
                    }
                  />
                  <button
                    type="button"
                    data-testid={`remove-sub-goal-${idx}`}
                    onClick={() => removeSubGoal(idx)}
                    aria-label={`Ta bort delmål ${idx + 1}`}
                    className="shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X size={16} />
                  </button>
                </div>
                {errors.sub_goals?.[idx] && (
                  <p role="alert" className="mt-1 text-xs text-destructive">
                    {errors.sub_goals[idx]}
                  </p>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            data-testid="add-sub-goal-btn"
            onClick={addSubGoal}
            className="flex items-center gap-1.5 text-sm font-medium hover:opacity-70 transition-opacity mb-6"
          >
            <Plus size={15} style={{ color: '#F9CF3F' }} />
            <span style={{ color: '#333333' }}>Lägg till delmål</span>
          </button>

          {/* ══ Sektion 4 — Fler inställningar (kollapsbar) ══ */}
          <button
            type="button"
            data-testid="advanced-toggle"
            onClick={() => setAdvOpen((v) => !v)}
            aria-expanded={advOpen}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            {advOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Fler inställningar
          </button>
          {advOpen && (
            <div
              className="mt-3 mb-4 pl-4 border-l-2 space-y-4"
              style={{ borderColor: '#F9CF3F' }}
              data-testid="advanced-section"
            >
              <div className="space-y-1.5">
                <Label htmlFor="g-tags">Taggar</Label>
                <Input
                  id="g-tags"
                  data-testid="goal-tags-input"
                  placeholder="t.ex. arbetsmarknad, praktik (kommaseparerat)"
                  value={form.tags}
                  onChange={(e) => set('tags', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* ══ Åtgärdsknappar ══ */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t mt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              disabled={submitting}
              className="rounded-full"
            >
              Avbryt
            </Button>
            <Button
              type="submit"
              data-testid="goal-submit-btn"
              disabled={submitting}
              className="rounded-full font-bold px-8"
              style={{ background: '#F9CF3F', color: '#333333' }}
            >
              {submitting ? 'Sparar...' : isEdit ? 'Uppdatera mål' : 'Skapa mål'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ── GoalsPage ──────────────────────────────────────────────────────────────────

const GoalsPage = () => {
  const { user }                        = useAuth();
  const location                        = useLocation();
  const [goals, setGoals]               = useState([]);
  const [participants, setParticipants] = useState([]);
  const [orgUsers, setOrgUsers]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [formOpen, setFormOpen]         = useState(false);
  const [editGoal, setEditGoal]         = useState(null);
  const [formInitial, setFormInitial]   = useState(null);
  const suggestionApplied               = useRef(false);

  const canWrite = WRITE_ROLES.includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const suggestion = location.state?.goalSuggestion;
    if (!suggestion || suggestionApplied.current) return;
    suggestionApplied.current = true;
    const subGoalTitles = suggestion.suggested_subgoals || [];
    setFormInitial({
      ...buildEmpty(),
      title:          suggestion.suggested_title || '',
      participant_id: suggestion.participant_id   || '',
      description:    subGoalTitles.join('\n'),
      assessment_id:  suggestion.assessment_id   || '',
      sub_goals:      subGoalTitles.map((t) => ({ id: newSgId(), title: t })),
    });
    setEditGoal(null);
    setFormOpen(true);
  }, [location.state]);

  const fetchData = async () => {
    try {
      const [goalsRes, participantsRes, usersRes] = await Promise.all([
        api.get('/goals'),
        api.get('/participants'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setGoals(goalsRes.data);
      setParticipants(participantsRes.data);
      setOrgUsers(usersRes.data || []);
    } catch {
      toast.error('Kunde inte hämta data');
    } finally {
      setLoading(false);
    }
  };

  const getParticipantName = (pid) =>
    participants.find((p) => p.id === pid)?.name || 'Okänd';

  const openCreate = () => { setEditGoal(null); setFormOpen(true); };
  const openEdit   = (goal) => { setEditGoal(goal); setFormOpen(true); };
  const closeForm  = () => { setFormOpen(false); setEditGoal(null); setFormInitial(null); };

  const handleSaved = async () => {
    closeForm();
    await fetchData();
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="goals-page">

        {/* ── Header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-black tracking-tight">Mål</h1>
            <p className="text-muted-foreground mt-1">Följ upp mål och framsteg</p>
          </div>
          {canWrite && (
            formOpen ? (
              <Button variant="ghost" className="rounded-full" onClick={closeForm}>
                <X size={16} className="mr-1.5" /> Avbryt
              </Button>
            ) : (
              <Button
                data-testid="create-goal-btn"
                className="rounded-full font-bold"
                onClick={openCreate}
              >
                <Plus size={18} className="mr-2" /> Nytt mål
              </Button>
            )
          )}
        </div>

        {/* ── Inline-formulär ── */}
        {canWrite && formOpen && (
          <GoalForm
            key={editGoal?.id ?? 'new'}
            initial={editGoal ? fromGoal(editGoal) : (formInitial || buildEmpty())}
            participants={participants}
            orgUsers={orgUsers}
            onSave={handleSaved}
            onCancel={closeForm}
            isEdit={!!editGoal}
          />
        )}

        {/* ── Mål-grid ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {loading ? (
            <Card className="col-span-full">
              <CardContent className="p-6 text-muted-foreground">Laddar mål...</CardContent>
            </Card>
          ) : goals.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="p-12 text-center">
                <TrendingUp size={48} className="mx-auto mb-4 text-muted-foreground/20" />
                <p className="text-muted-foreground text-lg">Inga mål ännu.</p>
                {canWrite && !formOpen && (
                  <Button className="mt-4 rounded-full font-bold" onClick={openCreate}>
                    <Plus size={16} className="mr-2" /> Skapa första målet
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            goals.map((goal) => {
              const progress = calculateProgress(goal.current_value, goal.target_value);
              return (
                <Card
                  key={goal.id}
                  data-testid={`goal-card-${goal.id}`}
                  className="hover:shadow-lg transition-shadow"
                >
                  <CardContent className="pt-5 pb-4 px-5 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-heading font-bold text-lg leading-tight">{goal.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {getParticipantName(goal.participant_id)}
                        </p>
                      </div>
                      {canWrite && (
                        <button
                          type="button"
                          data-testid={`edit-goal-${goal.id}`}
                          onClick={() => openEdit(goal)}
                          className="shrink-0 p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
                          aria-label={`Redigera mål: ${goal.title}`}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                    </div>
                    {goal.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">{goal.description}</p>
                    )}
                    {goal.target_value && (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Framsteg</span>
                          <span className="font-bold">{progress}%</span>
                        </div>
                        <Progress value={progress} className="h-2" />
                        <p className="text-xs text-muted-foreground">
                          {goal.current_value ?? 0} / {goal.target_value} {goal.unit}
                        </p>
                      </div>
                    )}
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                      goal.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      {goal.status === 'active' ? 'Aktiv' : goal.status}
                    </span>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export { GoalForm, buildEmpty, fromGoal };
export default GoalsPage;
